import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Save, Palette, Image as ImageIcon, MessageSquare, Layout as LayoutIcon, Info, Upload, RefreshCw, AlertTriangle, Megaphone } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, notifyError, imageUrl } from "@/lib/api";
import { resolveTenantLogo, validLogoScale } from "@/lib/logoResolver";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useBranding } from "@/context/BrandingContext";
import ImageUploadField from "@/components/admin/ImageUploadField";
import { pickUploadValue } from "@/lib/uploadValue";
import { TYPOGRAPHY_PRESETS, TYPOGRAPHY_CUSTOM, FONT_WHITELIST } from "@/lib/designTokens";
import {
  CANONICAL_TOKEN_KEYS,
  TOKEN_META,
  TOKEN_GROUPS,
  V2_DEFAULT_TOKENS,
  isStrictHex,
  contrastWarnings,
} from "@/lib/designTokensV2";

// Branding V2 — helper picker warna dengan fallback placeholder.
function ColorField({ label, value, onChange, fallback, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2 items-center">
        <input
          type="color"
          value={value || fallback}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-14 rounded border border-[#E5E5E5]"
          data-testid={`${testId}-picker`}
        />
        <Input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Default ${fallback}`}
          className="font-mono text-xs"
          data-testid={testId}
        />
      </div>
    </div>
  );
}

const LOGO_SCALE_OPTIONS = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium (default)" },
  { value: "large", label: "Large" },
  { value: "xl", label: "Extra Large" },
];

const LOGO_H_NOTES = [
  "Ukuran maksimal file: 2 MB",
  "Ukuran yang direkomendasikan: 1200 × 300 px (rasio 4:1)",
  "Rekomendasi alternatif: 1000 × 250 px",
  "Gunakan background transparan agar logo cocok di berbagai tema.",
  "Format: PNG, JPG, JPEG, atau WEBP",
];

// Kontrak nilai upload per field (audit Legacy Branding Image Public URL Hotfix):
//   "url"  → nilai dipakai sebagai URL publik oleh SSR `absolutize()`
//            (og:image fallback: og_image → logo; favicon → <link rel="icon">)
//   "path" → tidak ada konsumen SSR/backend; tetap storage key (default lama)
export const VISUAL_UPLOAD_FIELDS = [
  { field: "logo", label: "Logo (Utama)", valueField: "url" },
  { field: "logo_dark", label: "Logo Dark Mode (opsional)", valueField: "path" },
  { field: "favicon", label: "Favicon", valueField: "url" },
  { field: "og_image", label: "OG Image (untuk share sosmed)", valueField: "url" },
  { field: "default_banner", label: "Default Banner", valueField: "path" },
];


/**
 * Admin Branding Settings — White Label Foundation v1.
 *
 * 5 sections: General, Visual, Theme, Contact, Footer.
 * PUT /admin/settings/branding untuk save.
 * BrandingContext.reload() dipanggil setelah save supaya CSS vars + title update instant.
 */
const selectCls = "mt-1 h-10 w-full rounded-xl border border-[#E5E5E5] bg-white px-3 text-sm";

function StorefrontContentCard({ sf, upd }) {
  const set = (k, v) => upd("storefront", k, v);
  const on = (v) => v === "true";
  return (
    <Card className="p-6" data-testid="branding-storefront-section">
      <h3 className="font-display font-bold mb-1 flex items-center gap-2"><Megaphone size={18} className="brand-text-primary" /> Konten Storefront</h3>
      <p className="text-xs text-[#5C5C5C] mb-4">Bar pengumuman & ajakan bertindak di beranda. Tampil pada template yang mendukung (mis. Global Commerce Premium). Kosongkan untuk memakai teks netral bawaan.</p>
      <div className="grid gap-3">
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={on(sf.announcement_enabled)} onChange={(e) => set("announcement_enabled", e.target.checked ? "true" : "false")} data-testid="sf-announcement-enabled" />
          Tampilkan bar pengumuman
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Teks Pengumuman</Label><Input value={sf.announcement_text || ""} onChange={(e) => set("announcement_text", e.target.value)} placeholder="Tulis pengumuman singkat untuk pelanggan" maxLength={140} data-testid="sf-announcement-text" /></div>
          <div><Label>Tautan (opsional)</Label><Input value={sf.announcement_link || ""} onChange={(e) => set("announcement_link", e.target.value)} placeholder="/katalog atau https://..." data-testid="sf-announcement-link" /></div>
        </div>
        <hr className="my-1 border-[#EEE]" />
        <label className="flex items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={on(sf.cta_enabled)} onChange={(e) => set("cta_enabled", e.target.checked ? "true" : "false")} data-testid="sf-cta-enabled" />
          Tampilkan bagian ajakan (Business CTA) di beranda
        </label>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label>Label kecil</Label><Input value={sf.cta_badge || ""} onChange={(e) => set("cta_badge", e.target.value)} placeholder="PENAWARAN KHUSUS" maxLength={40} data-testid="sf-cta-badge" /></div>
          <div><Label>Judul</Label><Input value={sf.cta_title || ""} onChange={(e) => set("cta_title", e.target.value)} placeholder="Butuh Penawaran Khusus?" maxLength={90} data-testid="sf-cta-title" /></div>
          <div className="sm:col-span-2"><Label>Deskripsi</Label><Textarea value={sf.cta_description || ""} onChange={(e) => set("cta_description", e.target.value)} placeholder="Hubungi kami untuk mendapatkan informasi dan penawaran sesuai kebutuhan Anda." rows={2} maxLength={240} data-testid="sf-cta-description" /></div>
          <div><Label>Teks Tombol</Label><Input value={sf.cta_button_text || ""} onChange={(e) => set("cta_button_text", e.target.value)} placeholder="Hubungi Kami" maxLength={40} data-testid="sf-cta-button-text" /></div>
          <div>
            <Label>Aksi Tombol</Label>
            <select value={sf.cta_button_action || ""} onChange={(e) => set("cta_button_action", e.target.value)} className={selectCls} data-testid="sf-cta-action">
              <option value="">Otomatis (WhatsApp bila ada, jika tidak tautan)</option>
              <option value="whatsapp">WhatsApp (nomor dari Kontak)</option>
              <option value="link">Tautan (URL di bawah)</option>
            </select>
          </div>
          <div className="sm:col-span-2"><Label>URL Tujuan (untuk aksi Tautan)</Label><Input value={sf.cta_button_url || ""} onChange={(e) => set("cta_button_url", e.target.value)} placeholder="/katalog, https://..., mailto:..." data-testid="sf-cta-url" /></div>
          <div>
            <Label>Gaya Latar</Label>
            <select value={sf.cta_background_style || ""} onChange={(e) => set("cta_background_style", e.target.value)} className={selectCls} data-testid="sf-cta-bg-style">
              <option value="">Lembut (bawaan)</option>
              <option value="solid">Solid (warna utama)</option>
              <option value="image">Gambar latar</option>
            </select>
          </div>
        </div>
        {sf.cta_background_style === "image" ? (
          <div>
            <Label>Gambar Latar CTA</Label>
            <ImageUploadField value={sf.cta_background_image || ""} onChange={(v) => set("cta_background_image", v)} testId="sf-cta-bg-image" />
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export default function AdminBrandingSettings() {
  const { reload } = useBranding();
  const [b, setB] = useState({
    general: { name: "", short_name: "", tagline: "", description: "" },
    visual: { logo: "", logo_dark: "", logo_horizontal: "", logo_scale: "", favicon: "", og_image: "", default_banner: "", pwa_icon: "" },
    theme: { primary: "", secondary: "", accent: "", background: "", text: "", price_color: "", sale_badge_color: "", button_primary_color: "", button_secondary_color: "", typography_preset: "" },
    contact: { whatsapp: "", email: "", instagram: "", facebook: "", tiktok: "", youtube: "", address: "", maps_url: "" },
    footer: { text: "", copyright: "", powered_by: "" },
    storefront: {
      announcement_enabled: "", announcement_text: "", announcement_link: "",
      cta_enabled: "", cta_badge: "", cta_title: "", cta_description: "",
      cta_button_text: "", cta_button_action: "", cta_button_url: "",
      cta_background_style: "", cta_background_image: "",
    },
  });
  // Design System v2 (Phase 3) — effective 10-token palette displayed in
  // the "Brand Colors" section. Pre-populated from backend `design_tokens`
  // (resolver-derived). `v2Touched` tracks which keys the user has edited
  // so we only PATCH those on save (unedited fields stay inherited, so a
  // future template palette update still propagates).
  const [v2Tokens, setV2Tokens] = useState(V2_DEFAULT_TOKENS);
  const [v2Touched, setV2Touched] = useState(() => new Set());
  const initialV2Ref = useRef(V2_DEFAULT_TOKENS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/settings/branding");
      if (r.data) {
        setB(r.data);
        const dt = r.data.design_tokens || {};
        const next = { ...V2_DEFAULT_TOKENS };
        for (const k of CANONICAL_TOKEN_KEYS) {
          if (isStrictHex(dt[k])) next[k] = dt[k];
        }
        setV2Tokens(next);
        initialV2Ref.current = next;
        setV2Touched(new Set());
      }
    } catch (e) { notifyError(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (section, field, value) =>
    setB((x) => ({ ...x, [section]: { ...(x[section] || {}), [field]: value } }));

  // v2 token editor — always updates local display; only marks `touched`
  // when the value actually differs from the loaded (effective) value.
  const updV2 = (key, value) => {
    setV2Tokens((prev) => ({ ...prev, [key]: value }));
    setV2Touched((prev) => {
      if (value === initialV2Ref.current[key]) {
        // Reverting to inherited value → drop the override intent.
        if (!prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      }
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  // WCAG advisories on the currently-displayed palette (advisory only).
  const wcagWarnings = useMemo(() => contrastWarnings(v2Tokens), [v2Tokens]);

  const save = async () => {
    // Guard: validate touched v2 tokens locally before PUT (nicer UX than
    // waiting for backend 4xx). Empty string is allowed (= clear override).
    for (const k of v2Touched) {
      const v = (v2Tokens[k] || "").trim();
      if (v !== "" && !isStrictHex(v)) {
        toast.error(`${TOKEN_META[k]?.label || k}: HEX tidak valid (${v || "kosong"}). Gunakan format #RRGGBB.`);
        return;
      }
    }
    setSaving(true);
    try {
      // Merge v2 overrides (touched only) into the theme payload — the
      // backend does field-level $set so untouched keys survive.
      const themePayload = { ...(b.theme || {}) };
      for (const k of v2Touched) {
        themePayload[k] = (v2Tokens[k] || "").trim();
      }
      await api.put("/admin/settings/branding", { ...b, theme: themePayload });
      toast.success("Branding tersimpan. Halaman akan otomatis update.");
      await reload();
      await load(); // Re-fetch effective tokens (picks up resolver changes).
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  // Upload helper — reuse engine upload existing: POST /api/upload (returns {path, url}).
  // Default tetap storage `path`; field yang nilainya dipakai sebagai URL publik
  // (di-absolutize SSR) harus opt-in eksplisit { valueField: "url" }.
  const uploadAndSet = async (file, section, field, { valueField = "path" } = {}) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd(section, field, pickUploadValue(r.data, valueField));
      toast.success(`Upload ${field} berhasil`);
    } catch (e) { notifyError(e); }
  };

  // Sprint Tenant PWA Icon v1 — dedicated upload endpoint that validates
  // (square + >=512x512 + PNG/JPG/WebP) and generates 192/512 derivatives
  // server-side. Persists `settings.branding.visual.pwa_icon` directly, so
  // we update local state from the response WITHOUT going through the
  // generic branding PUT.
  const [pwaUploading, setPwaUploading] = useState(false);
  const uploadPwaIcon = async (file) => {
    if (!file) return;
    setPwaUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/branding/pwa-icon", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data?.pwa_icon || "";
      upd("visual", "pwa_icon", url);
      toast.success("Ikon PWA berhasil di-upload. Manifest akan otomatis update.");
      await reload();
    } catch (e) { notifyError(e); }
    finally { setPwaUploading(false); }
  };
  const resetPwaIcon = async () => {
    setPwaUploading(true);
    try {
      await api.delete("/admin/branding/pwa-icon");
      upd("visual", "pwa_icon", "");
      toast.success("Ikon PWA di-reset. Manifest kembali ke default.");
      await reload();
    } catch (e) { notifyError(e); }
    finally { setPwaUploading(false); }
  };

  if (loading) {
    return <AdminLayout><div className="p-8 text-center text-[#5C5C5C]">Memuat branding...</div></AdminLayout>;
  }

  return (
    <AdminLayout title="Platform Branding">
      <div className="max-w-4xl space-y-6" data-testid="admin-branding-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-black">Platform Branding</h1>
            <p className="text-sm text-[#5C5C5C] mt-1">Ubah identitas website. Semua perubahan berlaku instant tanpa deploy ulang.</p>
          </div>
          <Button onClick={save} disabled={saving} className="brand-btn-primary rounded-xl font-bold h-11 px-5" data-testid="branding-save-btn">
            <Save size={16} className="mr-2" /> {saving ? "Menyimpan..." : "Simpan Branding"}
          </Button>
        </div>

        {/* General */}
        <Card className="p-6" data-testid="branding-general-section" data-tour="branding-general">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><Info size={18} className="brand-text-primary" /> General</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Website Name</Label><Input value={b.general.name || ""} onChange={(e) => upd("general", "name", e.target.value)} placeholder="Toko Anda" data-testid="branding-name" /></div>
            <div><Label>Short Name (PWA/mobile)</Label><Input value={b.general.short_name || ""} onChange={(e) => upd("general", "short_name", e.target.value)} placeholder="TokoAnda" /></div>
            <div className="sm:col-span-2"><Label>Tagline</Label><Input value={b.general.tagline || ""} onChange={(e) => upd("general", "tagline", e.target.value)} placeholder="Segar & Terpercaya" /></div>
            <div className="sm:col-span-2"><Label>Short Description</Label><Textarea value={b.general.description || ""} onChange={(e) => upd("general", "description", e.target.value)} placeholder="Telur premium ..." rows={2} /></div>
          </div>
        </Card>

        {/* Logo Horizontal Storefront */}
        <Card className="p-6" data-testid="branding-logo-horizontal-section">
          <h3 className="font-display font-bold mb-1 flex items-center gap-2">
            <ImageIcon size={18} className="brand-text-primary" /> Logo Horizontal Storefront
          </h3>
          <p className="text-xs text-[#5C5C5C] mb-4">
            Upload logo horizontal yang sudah memuat nama brand dan tagline agar tampil rapi di header toko.
          </p>
          <ImageUploadField
            value={b.visual.logo_horizontal || ""}
            onChange={(v) => upd("visual", "logo_horizontal", v)}
            valueField="url"
            notes={LOGO_H_NOTES}
            testId="branding-logo-horizontal-upload"
          />
          {/* Branding V2 — Logo Scale Control */}
          <div className="mt-4">
            <Label>Logo Scale</Label>
            <select
              value={b.visual.logo_scale || "medium"}
              onChange={(e) => upd("visual", "logo_scale", e.target.value)}
              className="mt-1 h-10 w-full sm:w-64 rounded-xl border border-[#E5E5E5] bg-white px-3 text-sm"
              data-testid="branding-logo-scale-select"
            >
              {LOGO_SCALE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className="text-[11px] text-[#5C5C5C] mt-1.5">
              Perbesar/perkecil logo horizontal di header toko tanpa upload ulang. Tetap responsif & tidak overflow di mobile.
            </p>
            {(() => {
              // Unified resolver — preview identik dengan runtime (Logo.jsx).
              const r = resolveTenantLogo(b.visual, b.general?.name);
              return (
                <div className="mt-3 border border-dashed border-[#E5E5E5] rounded-xl p-3 bg-[#FAFAFA]" data-testid="branding-logo-scale-preview">
                  <div className="text-[10px] uppercase font-bold text-[#5C5C5C] mb-2">Preview (identik dengan storefront)</div>
                  {r.type === "horizontal" ? (
                    <img
                      src={r.src}
                      alt="Logo preview"
                      className="storefront-logo-h"
                      data-logo-scale={validLogoScale(b.visual.logo_scale)}
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : r.type === "initial" ? (
                    <span className="inline-grid place-items-center rounded-lg font-extrabold text-white" style={{ width: 42, height: 42, backgroundColor: "var(--brand-primary)", fontSize: 20 }}>
                      {r.initial}
                    </span>
                  ) : (
                    <img src={r.src} alt="Logo preview" className="h-10 w-10 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  )}
                </div>
              );
            })()}
          </div>
          <p className="text-[11px] text-[#5C5C5C] mt-3">
            Jika logo horizontal diisi, header toko hanya menampilkan logo ini — nama toko &amp; tagline teks tidak lagi ditampilkan terpisah.
            Kosongkan untuk kembali ke tampilan lama (logo kecil + nama + tagline).
          </p>
        </Card>

        {/* Visual */}
        <Card className="p-6" data-testid="branding-visual-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><ImageIcon size={18} className="brand-text-primary" /> Visual Assets</h3>
          <div className="space-y-3">
            {VISUAL_UPLOAD_FIELDS.map((it) => (
              <div key={it.field} className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                <div>
                  <Label>{it.label}</Label>
                  <Input
                    value={b.visual[it.field] || ""}
                    onChange={(e) => upd("visual", it.field, e.target.value)}
                    placeholder="/brand/... atau https://..."
                    className="font-mono text-xs"
                    data-testid={`branding-visual-${it.field}`}
                  />
                </div>
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadAndSet(e.target.files?.[0], "visual", it.field, { valueField: it.valueField })} />
                  <span className="inline-flex items-center gap-1 px-3 h-9 rounded-xl border border-[#E5E5E5] text-xs hover:bg-[#FAF9F6]"><Upload size={12} /> Upload</span>
                </label>
              </div>
            ))}
          </div>
        </Card>

        {/* Sprint Tenant PWA Icon v1 — Mobile App / PWA icon
            Compact section: preview + upload + reset. Persists directly via
            /api/admin/branding/pwa-icon (server validates + generates 192/512
            PNG derivatives). Falls back to platform icons when empty. */}
        <Card className="p-6" data-testid="branding-pwa-section">
          <h3 className="font-display font-bold mb-1 flex items-center gap-2">
            <ImageIcon size={18} className="brand-text-primary" /> Mobile App / PWA
          </h3>
          <p className="text-xs text-[#5C5C5C] mb-3">
            Ikon digunakan saat pelanggan menambahkan toko ke <b>layar utama perangkat</b> (Android / iOS) dan saat storefront ter-install sebagai PWA.
          </p>
          <div className="flex items-start gap-4 flex-wrap">
            <div
              className="w-20 h-20 rounded-2xl border border-[#E5E5E5] bg-[#FAFAFA] grid place-items-center overflow-hidden shrink-0"
              data-testid="branding-pwa-preview"
            >
              {b.visual.pwa_icon ? (
                <img
                  src={imageUrl(b.visual.pwa_icon)}
                  alt="Ikon PWA"
                  className="w-full h-full object-cover"
                  data-testid="branding-pwa-preview-img"
                />
              ) : (
                <span className="text-[10px] text-[#9CA3AF] text-center px-1">Belum ada ikon</span>
              )}
            </div>
            <div className="flex-1 min-w-[200px]">
              <ul className="text-xs text-[#5C5C5C] list-disc pl-4 space-y-1">
                <li>Gunakan gambar <b>persegi minimal 512 × 512 px</b>.</li>
                <li>Beri <b>ruang kosong ~10%</b> di sekitar logo agar ikon tidak terpotong pada beberapa perangkat Android (safe area untuk maskable).</li>
                <li>Format PNG (disarankan), JPG, atau WebP. Maks 2 MB.</li>
                <li>Jika kosong, Dynicty otomatis pakai ikon default platform.</li>
              </ul>
              <p className="text-[11px] text-[#9CA3AF] mt-2">
                Catatan iOS: ikon pada perangkat yang <i>sudah</i> memasang storefront ke Home Screen baru berubah setelah pengguna menghapus &amp; menambah ulang.
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    disabled={pwaUploading}
                    onChange={(e) => { uploadPwaIcon(e.target.files?.[0]); e.target.value = ""; }}
                    data-testid="branding-pwa-upload-input"
                  />
                  <span
                    className={`inline-flex items-center gap-1 px-3 h-9 rounded-xl border border-[#E5E5E5] text-xs ${pwaUploading ? "opacity-60" : "hover:bg-[#FAF9F6]"}`}
                    data-testid="branding-pwa-upload-btn"
                  >
                    <Upload size={12} /> {b.visual.pwa_icon ? "Ganti Ikon" : "Upload Ikon"}
                  </span>
                </label>
                {b.visual.pwa_icon && (
                  <button
                    type="button"
                    onClick={resetPwaIcon}
                    disabled={pwaUploading}
                    className="inline-flex items-center gap-1 px-3 h-9 rounded-xl border border-[#E5E5E5] text-xs hover:bg-[#FAF9F6] disabled:opacity-60"
                    data-testid="branding-pwa-reset-btn"
                  >
                    <RefreshCw size={12} /> Reset ke Default
                  </button>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Theme — Brand Colors (Design System v2 — 10 canonical tokens) */}
        <Card className="p-6" data-testid="branding-theme-section" data-tour="branding-colors">
          <h3 className="font-display font-bold mb-1 flex items-center gap-2"><Palette size={18} className="brand-text-primary" /> Brand Colors</h3>
          <p className="text-xs text-[#5C5C5C] mb-4">
            10 warna fungsional yang menentukan tampilan storefront — primary, permukaan (surface), teks, dan detail. Kosongkan input untuk memakai warna dari template. Setiap perubahan disimpan per-field: field yang tidak Anda edit tetap mengikuti template.
          </p>
          {TOKEN_GROUPS.map((grp) => (
            <div key={grp.id} className="mb-5" data-testid={`branding-v2-group-${grp.id}`}>
              <div className="text-[10px] uppercase font-bold tracking-widest text-[#5C5C5C] mb-2">{grp.title}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {grp.keys.map((k) => {
                  const meta = TOKEN_META[k];
                  const val = v2Tokens[k] || "";
                  const touched = v2Touched.has(k);
                  const invalid = touched && val !== "" && !isStrictHex(val);
                  return (
                    <div key={k}>
                      <Label className="flex items-center gap-1.5 text-xs">
                        <span>{meta.label}</span>
                        {touched && (
                          <span className="text-[9px] font-normal px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200" data-testid={`branding-v2-${k}-override-badge`}>OVERRIDE</span>
                        )}
                      </Label>
                      <div className="text-[10px] text-[#5C5C5C] mb-1.5" title={meta.hint}>{meta.hint}</div>
                      <div className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={isStrictHex(val) ? val : (initialV2Ref.current[k] || "#000000")}
                          onChange={(e) => updV2(k, e.target.value)}
                          className="h-10 w-14 rounded border border-[#E5E5E5] shrink-0"
                          data-testid={`branding-v2-${k}-picker`}
                        />
                        <Input
                          value={val}
                          onChange={(e) => updV2(k, e.target.value)}
                          placeholder="#RRGGBB"
                          className={`font-mono text-xs ${invalid ? "border-red-500 focus-visible:ring-red-500" : ""}`}
                          data-testid={`branding-v2-${k}`}
                        />
                      </div>
                      {invalid && (
                        <div className="text-[10px] text-red-600 mt-1">HEX tidak valid — gunakan format #RRGGBB</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {wcagWarnings.length > 0 && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1.5" data-testid="branding-v2-wcag-panel">
              <div className="flex items-center gap-1.5 text-xs font-bold text-amber-800">
                <AlertTriangle size={14} /> Peringatan Kontras (WCAG AA)
              </div>
              <div className="text-[10px] text-amber-700">Pasangan warna berikut mungkin sulit dibaca. Ini hanya peringatan — Anda tetap dapat menyimpan.</div>
              <ul className="text-[11px] text-amber-800 space-y-0.5">
                {wcagWarnings.map((w, i) => (
                  <li key={i} data-testid={`branding-v2-wcag-warning-${i}`}>
                    <span className="font-mono">{w.label}</span> — rasio <span className="font-bold">{w.ratio}:1</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="branding-v2-preview-swatches">
            {CANONICAL_TOKEN_KEYS.map((k) => (
              <div key={`prev-${k}`} className="flex items-center gap-1 text-[10px]">
                <span className="inline-block w-6 h-6 rounded border border-[#E5E5E5]" style={{ backgroundColor: v2Tokens[k] || "#fff" }} />
                <span className="font-bold">{TOKEN_META[k].label}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Branding V2 — Commerce Colors */}
        <Card className="p-6" data-testid="branding-commerce-section">
          <h3 className="font-display font-bold mb-1 flex items-center gap-2"><Palette size={18} className="brand-text-primary" /> Commerce Colors</h3>
          <p className="text-xs text-[#5C5C5C] mb-3">Warna harga & sale badge storefront. Kosongkan untuk default aman (menyesuaikan tema terang/gelap otomatis).</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <ColorField label="Price Color" value={b.theme.price_color} onChange={(v) => upd("theme", "price_color", v)} fallback="#111827" testId="branding-price-color" />
            <ColorField label="Sale Badge Color" value={b.theme.sale_badge_color} onChange={(v) => upd("theme", "sale_badge_color", v)} fallback="#EF4444" testId="branding-sale-badge-color" />
          </div>
          <div className="mt-4 flex items-center gap-4 border border-dashed border-[#E5E5E5] rounded-xl p-3 bg-[#FAFAFA]" data-testid="branding-commerce-preview">
            <span className="font-display font-black text-xl" style={{ color: b.theme.price_color || "#111827" }}>Rp 56.000</span>
            <span className="text-xs font-bold px-2.5 py-1 rounded-lg text-white" style={{ backgroundColor: b.theme.sale_badge_color || "#EF4444" }}>-20%</span>
            <span className="text-[10px] uppercase font-bold text-[#5C5C5C] ml-auto">Preview</span>
          </div>
        </Card>

        {/* Branding V2 — Action Colors */}
        <Card className="p-6" data-testid="branding-actions-section">
          <h3 className="font-display font-bold mb-1 flex items-center gap-2"><Palette size={18} className="brand-text-primary" /> Action Colors</h3>
          <p className="text-xs text-[#5C5C5C] mb-3">Warna tombol commerce — &ldquo;Beli Sekarang&rdquo;, &ldquo;Tambah&rdquo;, dan CTA. Tidak lagi otomatis mengikuti Primary Color.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <ColorField label="Primary Button Color" value={b.theme.button_primary_color} onChange={(v) => upd("theme", "button_primary_color", v)} fallback="#111827" testId="branding-button-primary-color" />
            <ColorField label="Secondary Button Color" value={b.theme.button_secondary_color} onChange={(v) => upd("theme", "button_secondary_color", v)} fallback="#F3F4F6" testId="branding-button-secondary-color" />
          </div>
          <div className="mt-4 flex items-center gap-3 border border-dashed border-[#E5E5E5] rounded-xl p-3 bg-[#FAFAFA]" data-testid="branding-actions-preview">
            <span className="text-xs font-bold px-4 py-2.5 rounded-xl" style={{ backgroundColor: b.theme.button_primary_color || "#111827", color: "#FFFFFF" }}>Beli Sekarang</span>
            <span className="text-xs font-bold px-4 py-2.5 rounded-xl border border-[#E5E5E5]" style={{ backgroundColor: b.theme.button_secondary_color || "#F3F4F6", color: "#111827" }}>+ Tambah</span>
            <span className="text-[10px] uppercase font-bold text-[#5C5C5C] ml-auto">Preview</span>
          </div>
        </Card>

        {/* Branding V2 — Typography Preset */}
        <Card className="p-6" data-testid="branding-typography-section">
          <h3 className="font-display font-bold mb-1 flex items-center gap-2"><Palette size={18} className="brand-text-primary" /> Typography</h3>
          <p className="text-xs text-[#5C5C5C] mb-3">Preset font storefront toko. Admin dashboard tidak ikut berubah. Kosongkan untuk font bawaan template.</p>
          <select
            value={b.theme.typography_preset || ""}
            onChange={(e) => upd("theme", "typography_preset", e.target.value)}
            className="h-10 w-full sm:w-72 rounded-xl border border-[#E5E5E5] bg-white px-3 text-sm"
            data-testid="branding-typography-preset-select"
          >
            <option value="">Default Template (Modern Commerce)</option>
            {Object.entries(TYPOGRAPHY_PRESETS).map(([k, p]) => (
              <option key={k} value={k}>{`${p.label} — ${p.heading}${p.body !== p.heading ? ` + ${p.body}` : ""}`}</option>
            ))}
            <option value={TYPOGRAPHY_CUSTOM}>Kustom — pilih Heading & Body Font</option>
          </select>
          {b.theme.typography_preset === TYPOGRAPHY_CUSTOM && (
            <div className="mt-3 grid sm:grid-cols-2 gap-3" data-testid="branding-typography-custom">
              {[["font_heading", "Heading Font", "branding-font-heading-select"], ["font_body", "Body Font", "branding-font-body-select"]].map(([key, label, tid]) => (
                <div key={key}>
                  <Label className="text-xs font-medium">{label}</Label>
                  <select value={FONT_WHITELIST.includes(b.theme[key]) ? b.theme[key] : ""} onChange={(e) => upd("theme", key, e.target.value)}
                    className="mt-1.5 h-10 w-full rounded-xl border border-[#E5E5E5] bg-white px-3 text-sm" data-testid={tid}>
                    <option value="">— Ikuti bawaan —</option>
                    {FONT_WHITELIST.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              ))}
              <p className="sm:col-span-2 text-[11px] text-[#5C5C5C]">Berlaku ke seluruh storefront (Company Profile, katalog, detail produk, keranjang/checkout, footer). Hanya font dari daftar aman ini yang diterima.</p>
              {(FONT_WHITELIST.includes(b.theme.font_heading) || FONT_WHITELIST.includes(b.theme.font_body)) && (
                <div className="sm:col-span-2 border border-dashed border-[#E5E5E5] rounded-xl p-3 bg-[#FAFAFA]" data-testid="branding-typography-custom-preview">
                  <link rel="stylesheet" href={`https://fonts.googleapis.com/css2?${[b.theme.font_heading, b.theme.font_body].filter((f) => FONT_WHITELIST.includes(f)).map((f) => `family=${encodeURIComponent(f)}:wght@400;700`).join("&")}&display=swap`} />
                  <div className="text-lg font-bold" style={{ fontFamily: `'${FONT_WHITELIST.includes(b.theme.font_heading) ? b.theme.font_heading : "inherit"}', sans-serif` }}>Judul Toko Premium</div>
                  <div className="text-sm text-[#5C5C5C]" style={{ fontFamily: `'${FONT_WHITELIST.includes(b.theme.font_body) ? b.theme.font_body : "inherit"}', sans-serif` }}>Deskripsi produk dan konten storefront menggunakan font ini.</div>
                </div>
              )}
            </div>
          )}
          {b.theme.typography_preset && TYPOGRAPHY_PRESETS[b.theme.typography_preset] && (
            <div className="mt-3 border border-dashed border-[#E5E5E5] rounded-xl p-3 bg-[#FAFAFA]" data-testid="branding-typography-preview">
              <div className="text-[10px] uppercase font-bold text-[#5C5C5C] mb-1">Preview — {TYPOGRAPHY_PRESETS[b.theme.typography_preset].label}</div>
              <div className="text-lg font-bold" style={{ fontFamily: `'${TYPOGRAPHY_PRESETS[b.theme.typography_preset].heading}', sans-serif` }}>
                Judul Toko Premium
              </div>
              <div className="text-sm text-[#5C5C5C]" style={{ fontFamily: `'${TYPOGRAPHY_PRESETS[b.theme.typography_preset].body}', sans-serif` }}>
                Deskripsi produk dan konten storefront menggunakan font ini.
              </div>
            </div>
          )}
        </Card>

        {/* Contact */}
        <Card className="p-6" data-testid="branding-contact-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><MessageSquare size={18} className="brand-text-primary" /> Kontak & Sosial Media</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>WhatsApp (62xxx)</Label><Input value={b.contact.whatsapp || ""} onChange={(e) => upd("contact", "whatsapp", e.target.value)} placeholder="6281234567890" data-testid="branding-whatsapp" /></div>
            <div><Label>Email</Label><Input value={b.contact.email || ""} onChange={(e) => upd("contact", "email", e.target.value)} placeholder="halo@tokoanda.com" data-testid="branding-email" /></div>
            <div><Label>Instagram</Label><Input value={b.contact.instagram || ""} onChange={(e) => upd("contact", "instagram", e.target.value)} placeholder="@tokoanda" /></div>
            <div><Label>Facebook</Label><Input value={b.contact.facebook || ""} onChange={(e) => upd("contact", "facebook", e.target.value)} placeholder="tokoanda.official" /></div>
            <div><Label>TikTok</Label><Input value={b.contact.tiktok || ""} onChange={(e) => upd("contact", "tiktok", e.target.value)} placeholder="@tokoanda" /></div>
            <div><Label>YouTube</Label><Input value={b.contact.youtube || ""} onChange={(e) => upd("contact", "youtube", e.target.value)} placeholder="@tokoanda" /></div>
            <div className="sm:col-span-2"><Label>Alamat</Label><Textarea value={b.contact.address || ""} onChange={(e) => upd("contact", "address", e.target.value)} rows={2} /></div>
            <div className="sm:col-span-2"><Label>Google Maps URL</Label><Input value={b.contact.maps_url || ""} onChange={(e) => upd("contact", "maps_url", e.target.value)} placeholder="https://maps.google.com/..." /></div>
          </div>
        </Card>

        {/* Footer */}
        <Card className="p-6" data-testid="branding-footer-section">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2"><LayoutIcon size={18} className="brand-text-primary" /> Footer</h3>
          <div className="grid gap-3">
            <div><Label>Footer Text</Label><Textarea value={b.footer.text || ""} onChange={(e) => upd("footer", "text", e.target.value)} placeholder="Deskripsi singkat brand..." rows={2} /></div>
            <div><Label>Copyright Owner</Label><Input value={b.footer.copyright || ""} onChange={(e) => upd("footer", "copyright", e.target.value)} placeholder="Nama Toko Anda" /></div>
            <div><Label>Powered By (optional)</Label><Input value={b.footer.powered_by || ""} onChange={(e) => upd("footer", "powered_by", e.target.value)} placeholder="e.g. Emergent Platform" /></div>
          </div>
        </Card>

        {/* Konten Storefront — Announcement Bar & Business CTA (dipakai template yang mendukung) */}
        <StorefrontContentCard sf={b.storefront || {}} upd={upd} />

        <div className="sticky bottom-4 flex gap-2">
          <Button onClick={save} disabled={saving} className="brand-btn-primary rounded-xl h-12 px-6 font-bold shadow-lg" data-testid="branding-save-btn-bottom">
            <Save size={16} className="mr-2" /> {saving ? "Menyimpan..." : "Simpan Branding"}
          </Button>
          <Button onClick={load} variant="outline" className="rounded-xl h-12" data-testid="branding-reload-btn">
            <RefreshCw size={16} className="mr-1" /> Reload
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
}
